<template>
  <div>
    <div class="d-flex flex-wrap align-center mb-6">
      <h3 class="text-h5 font-weight-bold white--text mr-auto">{{ heading }}</h3>
      <v-btn color="#58d388" class="black--text font-weight-bold" @click="openDialog()" rounded>
        <v-icon left>mdi-plus</v-icon> Modul erstellen
      </v-btn>
    </div>

    <!-- Tier-Sektionen -->
    <div v-for="tier in managerGroups" :key="tier.key" class="tier-section mb-8">
      <div class="d-flex align-center mb-3">
        <v-icon :color="tier.color" class="mr-2">{{ tier.icon }}</v-icon>
        <h4 class="text-h6 font-weight-bold" :style="{ color: tier.color }">{{ tier.label }}</h4>
        <v-spacer />
        <span class="text-caption grey--text">{{ groupedModules[tier.key].length }} Module · Drag zum Sortieren</span>
      </div>

      <div v-if="groupedModules[tier.key].length === 0" class="empty-tier rounded-lg pa-6 text-center grey--text">
        Noch keine Module in dieser Stufe.
      </div>

      <draggable
        v-else
        :list="groupedModules[tier.key]"
        :animation="200"
        handle=".drag-handle"
        ghost-class="ghost-card"
        @end="onReorder(tier.key)"
        class="d-flex flex-wrap module-grid"
        item-key="_id">
        <div v-for="mod in groupedModules[tier.key]" :key="mod._id" class="module-tile">
          <v-card class="module-card rounded-xl d-flex flex-column h-100" dark elevation="0">
            <div class="module-card__content">
              <div class="module-card__heading">
                <span class="drag-handle module-card__drag" title="Modul verschieben">
                  <v-icon color="grey">mdi-drag</v-icon>
                </span>
                <span class="module-card__icon" :style="{ color: tier.color }">
                  <v-icon>{{ mod.icon || "mdi-school" }}</v-icon>
                </span>
                <div class="module-card__copy">
                  <h5>{{ mod.title }}</h5>
                  <p class="module-card__meta">
                    {{ mod.lessons ? mod.lessons.length : 0 }} Lektionen · {{ mod.estimatedMinutes || 0 }} Min
                    · {{ evaluationSummary(mod) }}
                  </p>
                </div>
                <v-chip :color="mod.active ? '#4caf50' : 'grey darken-3'" small class="module-card__status">
                  {{ mod.active ? "Aktiv" : "Inaktiv" }}
                </v-chip>
              </div>

              <p class="module-card__description description-clip">{{ mod.description || "Keine Kurzbeschreibung hinterlegt." }}</p>

              <div v-if="!mod.lessons || mod.lessons.length === 0" class="module-card__notice">
                <v-icon>mdi-alert-circle-outline</v-icon>
                <span>Noch keine Lektionen hinzugefügt</span>
              </div>
            </div>

            <v-spacer />
            <v-divider class="border-white-05" />

            <div class="module-card__actions">
              <v-btn block depressed class="module-action module-action--primary" @click="openLessonEditor(mod)">
                <v-icon left>mdi-format-list-bulleted</v-icon>
                <span>Lektionen verwalten</span>
                <span class="module-action__count">{{ mod.lessons ? mod.lessons.length : 0 }}</span>
              </v-btn>
              <v-btn
                v-if="evaluationTypes(mod).includes('quiz')"
                block
                depressed
                class="module-action module-action--quiz"
                @click="openQuizEditor(mod)">
                <v-icon left>mdi-help-circle-outline</v-icon>
                <span>Wissenstest bearbeiten</span>
              </v-btn>
              <div class="module-card__secondary-actions">
                <v-btn text class="module-secondary-action" @click="openDialog(mod)">
                  <v-icon left>mdi-pencil-outline</v-icon>
                  Modul bearbeiten
                </v-btn>
                <v-btn text class="module-secondary-action module-secondary-action--danger" @click="confirmDelete(mod)">
                  <v-icon left>mdi-delete-outline</v-icon>
                  Löschen
                </v-btn>
              </div>
            </div>
          </v-card>
        </div>
      </draggable>
    </div>

    <!-- Create / Edit Dialog -->
    <v-dialog v-model="dialog" max-width="56rem" persistent dark scrollable content-class="module-dialog-shell">
      <v-card class="module-dialog">
        <header class="module-dialog__header">
          <div class="module-dialog__title-group">
            <span class="module-dialog__mark">
              <v-icon>{{ editingId ? "mdi-pencil-outline" : "mdi-book-plus-outline" }}</v-icon>
            </span>
            <div>
              <span class="module-dialog__eyebrow">{{ editingId ? "Modul konfigurieren" : "Neuer Lerninhalt" }}</span>
              <h2>{{ editingId ? "Modul bearbeiten" : "Neues Modul erstellen" }}</h2>
              <p>
                {{ editingId ? "Inhalte, Darstellung und Sichtbarkeit anpassen." : "Erfasse zuerst die Grunddaten. Danach öffnen wir direkt die Lektionen." }}
              </p>
            </div>
          </div>
          <v-btn
            icon
            class="module-dialog__close"
            aria-label="Dialog schliessen"
            :disabled="uploadingToolbox || savingModule"
            @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </header>

        <v-card-text class="module-dialog__body">
          <v-form ref="form" v-model="valid">
            <section class="dialog-section">
              <div class="dialog-section__heading">
                <span class="dialog-section__number">1</span>
                <div>
                  <h3>Grunddaten</h3>
                  <p>Name, Einordnung und Beschreibung des Moduls.</p>
                </div>
              </div>

              <v-row dense>
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="form.title"
                    label="Titel"
                    :rules="[(v) => !!v || 'Titel ist erforderlich']"
                    required
                    outlined
                    dense></v-text-field>
                </v-col>
                <v-col v-if="!isLeadership" cols="12" md="4">
                  <v-select
                    v-model="form.tier"
                    :items="['bronze', 'silber', 'gold']"
                    label="Stufe"
                    :rules="[(v) => !!v || 'Stufe erforderlich']"
                    outlined
                    dense
                    required></v-select>
                </v-col>
              </v-row>

              <v-select
                v-model="form.evaluationTypes"
                class="evaluation-select"
                :items="evaluationOptions"
                item-text="label"
                item-value="value"
                label="Erforderliche Modulabschlüsse"
                hint="Alle gewählten Prüfungsteile müssen bestanden sein. «Gelesen und verstanden» ist eine Alternative ohne Prüfung."
                multiple
                chips
                deletable-chips
                persistent-hint
                outlined
                dense
                :rules="[(value) => (value && value.length > 0) || 'Mindestens ein Modulabschluss erforderlich']"
                @change="onEvaluationChange" />
              <v-textarea
                v-for="type in manualEvaluationTypes"
                :key="type"
                v-model="form.evaluationInstructionsByType[type]"
                :label="type === 'video_submission' ? 'Auftrag für die Video-Abgabe' : 'Auftrag für die Praxisaufgabe'"
                :hint="type === 'video_submission' ? 'Diese Anleitung sehen Führungskräfte direkt vor der Video-Auswahl.' : 'Diese Anleitung sehen Mitarbeitende direkt vor dem PDF-Upload.'"
                persistent-hint
                outlined
                rows="3" />

              <div v-if="editingId && addedEvaluationTypes.length" class="evaluation-retroactive">
                <v-switch
                  v-model="form.evaluationsRequiredForCompletedUsers"
                  label="Neue Prüfungsteile auch für bisherige Absolventen verpflichtend"
                  color="#58d388"
                  hide-details />
                <p>{{ form.evaluationsRequiredForCompletedUsers
                  ? 'Abgeschlossene Module werden bei fehlenden Prüfungsteilen wieder geöffnet. Abhängige Lizenzen werden geprüft. Erledigte Lektionen und bestandene Tests bleiben erhalten.'
                  : 'Bisherige Abschlüsse bleiben gültig. Die neuen Prüfungsteile werden diesen Personen erlassen.' }}</p>
              </div>

              <v-textarea
                v-model="form.description"
                label="Kurzbeschreibung"
                hint="Wird auf der Modul-Karte und in der Übersicht angezeigt"
                outlined
                rows="2"
                dense
                persistent-hint></v-textarea>

              <v-textarea
                v-model="form.longDescription"
                label="Was lerne ich in diesem Modul?"
                hint="Die ausführliche Beschreibung wird oberhalb der Lektionen angezeigt"
                outlined
                rows="4"
                dense
                persistent-hint></v-textarea>
            </section>

            <section class="dialog-section">
              <div class="dialog-section__heading">
                <span class="dialog-section__number">2</span>
                <div>
                  <h3>Darstellung &amp; Freigabe</h3>
                  <p>Icon wählen und festlegen, wann Nutzer das Modul sehen.</p>
                </div>
              </div>

              <v-row dense align="stretch">
                <v-col cols="12" md="7">
                  <v-select v-model="form.icon" :items="iconOptions" label="Modul-Icon" outlined dense hide-details>
                    <template v-slot:selection="{ item }">
                      <v-icon left>{{ item }}</v-icon> {{ item }}
                    </template>
                    <template v-slot:item="{ item }">
                      <v-icon left>{{ item }}</v-icon> {{ item }}
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" md="5">
                  <div class="auto-duration">
                    <v-icon>mdi-clock-outline</v-icon>
                    <span>Dauer wird automatisch aus den Lektionen berechnet</span>
                  </div>
                </v-col>
              </v-row>

              <div class="visibility-panel">
                <div>
                  <h4>Für Nutzer sichtbar</h4>
                  <p>{{ activationHint }}</p>
                </div>
                <v-switch
                  v-model="form.active"
                  color="#58d388"
                  :disabled="!editingId"
                  hide-details
                  aria-label="Modul für Nutzer sichtbar schalten"></v-switch>
              </div>
            </section>
          </v-form>

          <section v-if="editingId" class="dialog-section dialog-section--toolbox">
            <div class="dialog-section__heading dialog-section__heading--with-count">
              <span class="dialog-section__number">3</span>
              <div>
                <h3>Toolbox &amp; Materialien</h3>
                <p>Dateien, Medien oder Notizen ergänzen. Videos bis 5 GB und 60 Minuten, andere Dateien bis 250 MB.</p>
              </div>
              <v-chip small color="grey darken-3">{{ toolbox.length }}</v-chip>
            </div>

            <v-row dense class="mb-1">
              <v-col cols="12" md="5">
                <v-file-input
                  v-model="newToolboxFile"
                  label="Datei (PDF, Bild, Video, Audio, …)"
                  outlined
                  dense
                  hide-details
                  :disabled="uploadingToolbox"
                  prepend-icon="mdi-paperclip"></v-file-input>
              </v-col>
              <v-col cols="12" md="5">
                <v-text-field
                  v-model="newToolboxTitle"
                  label="Anzeige-Titel (optional)"
                  outlined
                  dense
                  hide-details
                  :disabled="uploadingToolbox"
                  prepend-icon="mdi-tag-outline"></v-text-field>
              </v-col>
              <v-col cols="12" md="2" class="d-flex align-center">
                <v-btn
                  block
                  color="#58d388"
                  class="black--text module-dialog__upload"
                  :disabled="!newToolboxFile && !newToolboxText"
                  :loading="uploadingToolbox"
                  @click="uploadToolbox">
                  <v-icon left>mdi-upload</v-icon>
                  Hinzufügen
                </v-btn>
              </v-col>
            </v-row>

            <v-textarea
              v-model="newToolboxText"
              label="...oder einen Text / eine Notiz hinzufügen"
              outlined
              dense
              rows="2"
              :disabled="uploadingToolbox"
              class="mb-4"></v-textarea>

            <v-progress-linear v-if="uploadingToolbox" :value="toolboxProgress" color="#58d388" height="6" rounded class="mb-3"></v-progress-linear>

            <div v-if="toolbox.length === 0" class="toolbox-empty">Noch keine Materialien hinzugefügt.</div>

            <div v-else class="toolbox-grid">
              <v-card v-for="item in toolbox" :key="item._id" class="toolbox-item rounded-lg pa-3" dark elevation="0">
                <div class="d-flex align-start">
                  <div class="toolbox-icon mr-3" :class="`cat-${item.category}`">
                    <v-icon size="28" :color="categoryColor(item.category)">{{ categoryIcon(item.category) }}</v-icon>
                  </div>
                  <div class="flex-grow-1" style="min-width: 0">
                    <div v-if="editingTitleId !== item._id" class="d-flex align-center">
                      <span class="font-weight-bold text-truncate flex-grow-1" :title="item.title || item.originalName">
                        {{ item.title || item.originalName }}
                      </span>
                      <v-btn icon small color="grey" class="ml-1" @click.stop="startEditTitle(item)">
                        <v-icon small>mdi-pencil-outline</v-icon>
                      </v-btn>
                    </div>
                    <div v-else class="d-flex align-center">
                      <v-text-field
                        v-model="editingTitleValue"
                        dense
                        hide-details
                        autofocus
                        outlined
                        color="#58d388"
                        style="font-size: 0.875rem"
                        @keydown.enter="saveTitle(item)"
                        @keydown.esc="cancelEditTitle"></v-text-field>
                      <v-btn icon small color="#58d388" class="ml-1" @click.stop="saveTitle(item)" :loading="savingTitle">
                        <v-icon small>mdi-check</v-icon>
                      </v-btn>
                      <v-btn icon small color="grey" @click.stop="cancelEditTitle">
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </div>
                    <div class="toolbox-item__meta">
                      <span class="text-uppercase">{{ item.category }}</span>
                      <template v-if="item.size"> · {{ formatSize(item.size) }}</template>
                    </div>
                  </div>
                  <v-btn v-if="item.category !== 'text'" icon small @click="previewItem(item)">
                    <v-icon small>mdi-eye-outline</v-icon>
                  </v-btn>
                  <v-btn icon small color="red" @click="deleteToolbox(item)">
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </div>

                <div v-if="item.category === 'image'" class="toolbox-preview mt-2">
                  <SecuredMedia :src="toolboxPath(item)" media-type="image" />
                </div>
                <div v-else-if="item.category === 'text'" class="toolbox-item__text text-clip-3">{{ item.textContent }}</div>
              </v-card>
            </div>
          </section>

          <section v-else class="module-next-step">
            <span class="module-next-step__icon"><v-icon>mdi-format-list-bulleted</v-icon></span>
            <div>
              <span class="module-next-step__eyebrow">Nächster Schritt</span>
              <h3>Lektionen hinzufügen</h3>
              <p>Nach dem Erstellen öffnen wir direkt den Lektionen-Bereich dieses Moduls. Materialien kannst du später über „Modul bearbeiten“ ergänzen.</p>
            </div>
          </section>
        </v-card-text>

        <v-card-actions class="module-dialog__actions">
          <v-btn text class="module-dialog__cancel" :disabled="uploadingToolbox || savingModule" @click="closeDialog">Abbrechen</v-btn>
          <v-btn
            color="#58d388"
            class="black--text module-dialog__save"
            :loading="savingModule"
            :disabled="!valid || uploadingToolbox || savingModule"
            @click="saveModule">
            <v-icon left>{{ editingId ? "mdi-content-save-outline" : "mdi-arrow-right" }}</v-icon>
            {{ editingId ? "Änderungen speichern" : "Erstellen & Lektionen öffnen" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Preview Dialog -->
    <v-dialog v-model="previewDialog.show" max-width="900" dark>
      <v-card v-if="previewDialog.item" style="background-color: #1e1e1e">
        <v-card-title>
          {{ previewDialog.item.title || previewDialog.item.originalName }}
          <v-spacer />
          <v-btn icon @click="previewDialog.show = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <SecuredMedia
            :src="toolboxPath(previewDialog.item)"
            :media-type="previewDialog.item.category"
            :stream-playback="previewDialog.item.category === 'video'"
            :download-name="previewDialog.item.originalName"
            eager />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="6000" top right>
      {{ snackbar.text }}
    </v-snackbar>

    <LessonEditor v-model="lessonDialog" :module="selectedModule" />
    <QuizEditor v-model="quizDialog" :module="selectedModule" />
  </div>
</template>

<script>
import api from "@/api"
import { EVALUATION_LABELS, getEvaluationTypes, getEvaluationInstructions } from "@/utils/academyEvaluation"
import { getMediaSizeError, uploadCloudflareMedia } from "@/services/cloudflareMediaUploadService"
import draggable from "vuedraggable"
import LessonEditor from "./LessonEditor.vue"
import QuizEditor from "./QuizEditor.vue"
import SecuredMedia from "../SecuredMedia.vue"

export default {
  name: "ModuleManager",
  components: { LessonEditor, QuizEditor, draggable, SecuredMedia },
  props: {
    track: { type: String, default: "license" },
    heading: { type: String, default: "Module Manager" },
  },
  data() {
    return {
      modules: [],
      loading: false,
      dialog: false,
      lessonDialog: false,
      quizDialog: false,
      valid: false,
      editingId: null,
      selectedModule: null,
      originalEvaluationTypes: [],

      // Toolbox
      toolbox: [],
      newToolboxFile: null,
      toolboxUploadSession: null,
      toolboxUploadComplete: false,
      newToolboxText: "",
      newToolboxTitle: "",
      uploadingToolbox: false,
      toolboxProgress: 0,
      previewDialog: { show: false, item: null },
      editingTitleId: null,
      editingTitleValue: "",
      savingTitle: false,
      savingModule: false,

      form: {
        title: "",
        description: "",
        longDescription: "",
        tier: "bronze",
        track: this.track,
        icon: "mdi-school",
        evaluationTypes: ["quiz"],
        evaluationInstructionsByType: { video_submission: "", document_submission: "" },
        evaluationsRequiredForCompletedUsers: false,
        active: false,
      },

      iconOptions: [
        "mdi-school",
        "mdi-book-open-page-variant",
        "mdi-dumbbell",
        "mdi-heart-pulse",
        "mdi-brain",
        "mdi-chart-bar",
        "mdi-star",
        "mdi-certificate",
      ],

      tiers: [
        { key: "bronze", label: "Bronze", color: "#CD7F32", icon: "mdi-medal-outline" },
        { key: "silber", label: "Silber", color: "#C0C0C0", icon: "mdi-medal-outline" },
        { key: "gold", label: "Gold", color: "#FFD700", icon: "mdi-medal" },
      ],

      snackbar: { show: false, color: "success", text: "" },
    }
  },
  computed: {
    isLeadership() {
      return this.track === "leadership"
    },
    managerGroups() {
      if (this.isLeadership) {
        return [{ key: "leadership", label: "Leadership Coaching", color: "#7ee5a6", icon: "mdi-account-tie-outline" }]
      }
      return this.tiers
    },
    evaluationOptions() {
      const options = [
        { label: "Multiple-Choice-Wissenstest", value: "quiz" },
        { label: "Praxisaufgabe als PDF mit manueller Prüfung", value: "document_submission" },
        { label: "Gelesen und verstanden – ohne Test", value: "acknowledgement" },
      ]
      if (this.isLeadership) options.splice(1, 0, { label: "Video-Abgabe mit manueller Prüfung", value: "video_submission" })
      return options
    },
    activationHint() {
      if (this.form.evaluationTypes.includes("quiz")) return "Aktivierung erst nach Quiz-Erstellung möglich. Erfasse auch die Aufträge für weitere gewählte Prüfungsteile."
      if (this.manualEvaluationTypes.length) return "Aktiviere das Modul nach dem Erfassen der Lektionen und der gewählten Praxisaufträge."
      return "Aktiviere das Modul nach dem Erfassen aller Lektionen. Der Abschluss erfolgt über den Bestätigungsbutton."
    },
    manualEvaluationTypes() {
      return this.form.evaluationTypes.filter((type) => ["video_submission", "document_submission"].includes(type))
    },
    addedEvaluationTypes() {
      return this.form.evaluationTypes.filter((type) => !this.originalEvaluationTypes.includes(type))
    },
    groupedModules() {
      const groups = {}
      this.managerGroups.forEach((group) => {
        groups[group.key] = []
      })
      ;(this.modules || []).forEach((m) => {
        const matchesTrack = this.isLeadership ? m.track === "leadership" : !m.track || m.track === "license"
        if (!matchesTrack) return
        const groupKey = this.isLeadership ? "leadership" : m.tier
        if (groups[groupKey]) groups[groupKey].push(m)
      })
      Object.keys(groups).forEach((k) => groups[k].sort((a, b) => (a.order || 0) - (b.order || 0)))
      return groups
    },
  },
  mounted() {
    this.loadModules()
  },
  watch: {
    lessonDialog(isOpen) {
      if (!isOpen) this.loadModules()
    },
    newToolboxFile() {
      const previousSession = this.toolboxUploadSession
      this.toolboxUploadSession = null
      this.toolboxUploadComplete = false
      if (previousSession) this.removePreparedToolboxMedia(previousSession)
    },
  },
  methods: {
    notify(color, text) {
      this.snackbar = { show: true, color, text }
    },
    closeDialog() {
      if (this.uploadingToolbox) return
      const session = this.toolboxUploadSession
      this.toolboxUploadSession = null
      this.toolboxUploadComplete = false
      this.dialog = false
      if (session) this.removePreparedToolboxMedia(session)
    },
    removePreparedToolboxMedia(session) {
      if (!this.editingId || !session) return
      api.akademie.cancelModuleMediaUpload(this.editingId, "toolbox", session).catch((error) => {
        console.warn("Nicht verwendeter Toolbox-Upload konnte nicht entfernt werden", error)
      })
    },
    async loadModules() {
      this.loading = true
      try {
        this.modules = (await api.akademie.fetchModules({ track: this.track })) || []
      } catch (e) {
        console.error("Failed to load modules", e)
      } finally {
        this.loading = false
      }
    },
    async openDialog(item = null) {
      if (item) {
        try {
          item = await api.akademie.fetchModule(item._id)
        } catch (error) {
          this.notify("error", "Die aktuellen Moduleinstellungen konnten nicht geladen werden.")
          return
        }
        this.editingId = item._id
        this.originalEvaluationTypes = getEvaluationTypes(item)
        this.form = {
          title: item.title || "",
          description: item.description || "",
          longDescription: item.longDescription || "",
          tier: item.tier || "bronze",
          track: item.track || this.track,
          icon: item.icon || "mdi-school",
          evaluationTypes: [...this.originalEvaluationTypes],
          evaluationInstructionsByType: {
            video_submission: getEvaluationInstructions(item, "video_submission"),
            document_submission: getEvaluationInstructions(item, "document_submission"),
          },
          evaluationsRequiredForCompletedUsers: false,
          active: !!item.active,
        }
        this.toolbox = item.toolbox || []
      } else {
        this.editingId = null
        this.originalEvaluationTypes = []
        this.form = {
          title: "",
          description: "",
          longDescription: "",
          tier: "bronze",
          track: this.track,
          icon: "mdi-school",
          evaluationTypes: ["quiz"],
          evaluationInstructionsByType: { video_submission: "", document_submission: "" },
          evaluationsRequiredForCompletedUsers: false,
          active: false,
        }
        this.toolbox = []
      }
      this.newToolboxFile = null
      this.toolboxUploadSession = null
      this.toolboxUploadComplete = false
      this.newToolboxText = ""
      this.newToolboxTitle = ""
      this.dialog = true
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation()
      })
    },
    openLessonEditor(item) {
      this.selectedModule = item
      this.lessonDialog = true
    },
    openQuizEditor(item) {
      this.selectedModule = item
      this.quizDialog = true
    },
    async saveModule() {
      if (this.savingModule || !this.$refs.form || !this.$refs.form.validate()) return
      const isEdit = !!this.editingId
      this.savingModule = true
      try {
        if (isEdit) {
          await api.akademie.updateModule(this.editingId, this.form)
          await this.loadModules()
          this.closeDialog()
          this.notify("success", "Modul gespeichert")
          return
        }

        const data = { ...this.form, active: false }
        const created = await api.akademie.createModule(data)
        await this.loadModules()

        const createdId = created && (created._id || created.id)
        const createdModule = createdId
          ? this.modules.find((item) => (item._id || item.id) === createdId) || created
          : null

        this.dialog = false
        if (!createdModule) {
          this.notify("success", "Modul erstellt")
          return
        }

        this.notify("success", "Modul erstellt. Füge jetzt mindestens eine Lektion hinzu.")
        this.$nextTick(() => this.openLessonEditor(createdModule))
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.error) || "Speichern fehlgeschlagen"
        this.notify("error", msg)
      } finally {
        this.savingModule = false
      }
    },
    async confirmDelete(item) {
      if (!confirm(`Möchten Sie das Modul "${item.title}" wirklich löschen?`)) return
      try {
        await api.akademie.deleteModule(item._id)
        this.loadModules()
        this.notify("success", "Modul gelöscht")
      } catch (e) {
        this.notify("error", "Löschen fehlgeschlagen")
      }
    },

    async onReorder(tierKey) {
      const ids = this.groupedModules[tierKey].map((m) => m._id)
      try {
        await api.akademie.reorderModules(this.isLeadership ? "bronze" : tierKey, ids, this.track)
        // Local order sync
        this.groupedModules[tierKey].forEach((m, i) => (m.order = i))
        this.notify("success", "Reihenfolge gespeichert")
      } catch (e) {
        this.notify("error", "Sortierung konnte nicht gespeichert werden")
        this.loadModules()
      }
    },
    evaluationTypes: getEvaluationTypes,
    evaluationSummary(item) {
      return getEvaluationTypes(item).map((type) => EVALUATION_LABELS[type]).join(" + ")
    },
    onEvaluationChange(values) {
      if (values.includes("acknowledgement") && values.length > 1) {
        this.form.evaluationTypes = values[values.length - 1] === "acknowledgement" ? ["acknowledgement"] : values.filter((type) => type !== "acknowledgement")
      }
      if (!this.addedEvaluationTypes.length) this.form.evaluationsRequiredForCompletedUsers = false
    },
    // --- Toolbox ---
    async uploadToolbox() {
      if (!this.editingId) return
      this.uploadingToolbox = true
      this.toolboxProgress = 0
      try {
        const payload = { title: this.newToolboxTitle || null }
        if (this.newToolboxFile) {
          const sizeError = getMediaSizeError(this.newToolboxFile)
          if (sizeError) throw new Error(sizeError)
          if (!this.toolboxUploadSession) {
            this.toolboxUploadSession = await api.akademie.prepareModuleMediaUpload(this.editingId, "toolbox", this.newToolboxFile)
          }
          if (!this.toolboxUploadComplete) {
            await uploadCloudflareMedia(this.newToolboxFile, this.toolboxUploadSession, {
              onProgress: (loaded, total) => {
                this.toolboxProgress = Math.round((loaded * 100) / Math.max(1, total || this.newToolboxFile.size))
              },
            })
            this.toolboxUploadComplete = true
          }
          payload.media = this.toolboxUploadSession
        } else {
          payload.textContent = this.newToolboxText
        }
        const item = await api.akademie.uploadToolboxItem(this.editingId, payload)
        this.toolbox.push(item)
        this.toolboxUploadSession = null
        this.toolboxUploadComplete = false
        this.newToolboxFile = null
        this.newToolboxText = ""
        this.newToolboxTitle = ""
        this.notify("success", "Eintrag hinzugefügt")
      } catch (e) {
        const message = (e.response && e.response.data && e.response.data.error) || e.message || "Upload fehlgeschlagen"
        this.notify("error", message)
      } finally {
        this.uploadingToolbox = false
      }
    },
    startEditTitle(item) {
      this.editingTitleId = item._id
      this.editingTitleValue = item.title || item.originalName || ""
    },
    cancelEditTitle() {
      this.editingTitleId = null
      this.editingTitleValue = ""
    },
    async saveTitle(item) {
      this.savingTitle = true
      try {
        const updated = await api.akademie.updateToolboxItem(this.editingId, item._id, { title: this.editingTitleValue })
        item.title = updated.title
        this.cancelEditTitle()
        this.notify("success", "Titel gespeichert")
      } catch (e) {
        this.notify("error", "Speichern fehlgeschlagen")
      } finally {
        this.savingTitle = false
      }
    },
    async deleteToolbox(item) {
      if (!confirm(`"${item.title || item.originalName}" wirklich löschen?`)) return
      try {
        await api.akademie.deleteToolboxItem(this.editingId, item._id)
        this.toolbox = this.toolbox.filter((t) => t._id !== item._id)
        this.notify("success", "Eintrag gelöscht")
      } catch (e) {
        this.notify("error", "Löschen fehlgeschlagen")
      }
    },
    toolboxPath(item) {
      return item.category === "video"
        ? api.akademie.getToolboxItemPlaybackPath(this.editingId, item._id)
        : api.akademie.getToolboxItemPath(this.editingId, item._id)
    },
    previewItem(item) {
      this.previewDialog = { show: true, item }
    },
    categoryIcon(cat) {
      const map = {
        image: "mdi-image-outline",
        video: "mdi-video-outline",
        audio: "mdi-music-note-outline",
        pdf: "mdi-file-pdf-box",
        text: "mdi-text-box-outline",
        file: "mdi-file-outline",
      }
      return map[cat] || "mdi-file-outline"
    },
    categoryColor(cat) {
      const map = {
        image: "#42a5f5",
        video: "#ef5350",
        audio: "#ab47bc",
        pdf: "#e53935",
        text: "#66bb6a",
        file: "#bdbdbd",
      }
      return map[cat] || "#bdbdbd"
    },
    formatSize(b) {
      if (!b) return ""
      if (b < 1024) return b + " B"
      if (b < 1024 * 1024) return (b / 1024).toFixed(1) + " KB"
      return (b / 1024 / 1024).toFixed(1) + " MB"
    },
  },
}
</script>

<style scoped>
.evaluation-retroactive {
  margin-bottom: 1.25rem;
  padding: 0.25rem 1rem 1rem;
  border: 1px solid rgba(88, 211, 136, 0.2);
  border-radius: 0.875rem;
}

.evaluation-retroactive p {
  margin: 0.625rem 0 0;
  color: #b8b8bc;
  font-size: 0.875rem;
  line-height: 1.55;
}

.evaluation-select ::v-deep .v-chip {
  max-width: 100%;
  height: auto;
  min-height: 2rem;
  padding-top: 0.375rem;
  padding-bottom: 0.375rem;
  font-size: 0.875rem;
}

.evaluation-select ::v-deep .v-chip__content {
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
}

.gold-text {
  background: linear-gradient(45deg, #58d388, #7ee5a6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.tier-section {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 1rem;
  padding: 1rem 1rem 0.5rem;
}

.module-grid {
  gap: 1rem;
}

.module-tile {
  flex: 1 1 22rem;
  max-width: 28rem;
  min-width: 18rem;
}

.module-card {
  background: rgba(30, 30, 30, 0.85) !important;
  border: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 1rem;
  overflow: hidden;
  transition: transform 0.2s, border-color 0.2s;
}

.module-card:hover {
  border-color: rgba(88, 211, 136, 0.42);
  transform: translateY(-2px);
}

.module-card__content {
  padding: 1.25rem 1.25rem 1rem;
}

.module-card__heading {
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr) auto;
  align-items: start;
  gap: 0.75rem;
}

.module-card__drag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  min-height: 2.75rem;
}

.module-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 2.75rem;
  border-radius: 0.75rem;
  background: rgba(88, 211, 136, 0.1);
}

.module-card__icon .v-icon {
  color: inherit;
}

.module-card__copy {
  min-width: 0;
  padding-top: 0.1rem;
}

.module-card__copy h5 {
  margin: 0;
  color: #fff;
  font-size: 1.0625rem;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.module-card__meta {
  margin: 0.25rem 0 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
  line-height: 1.45;
}

.module-card__status {
  align-self: start;
  margin: 0 !important;
}

.module-card__status ::v-deep .v-chip__content {
  font-size: 0.75rem;
}

.module-card__description {
  min-height: 2.7rem;
  margin: 1rem 0 0;
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.875rem;
  line-height: 1.55;
  white-space: break-spaces;
  overflow-wrap: anywhere;
}

.module-card__notice {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.9rem;
  padding: 0.65rem 0.75rem;
  border: 1px solid rgba(255, 193, 7, 0.2);
  border-radius: 0.65rem;
  background: rgba(255, 193, 7, 0.07);
  color: #ffd667;
  font-size: 0.875rem;
  line-height: 1.4;
}

.module-card__notice .v-icon {
  color: currentColor;
  font-size: 1.2rem;
}

.module-card__actions {
  display: grid;
  gap: 0.65rem;
  padding: 1rem 1.25rem 1.15rem;
  background: rgba(0, 0, 0, 0.14);
}

.module-action {
  min-height: 2.75rem !important;
  border: 1px solid transparent;
  border-radius: 0.7rem;
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.875rem;
  font-weight: 700;
}

.module-action--primary {
  border-color: rgba(88, 211, 136, 0.28);
  background: rgba(88, 211, 136, 0.14) !important;
  color: #7ee5a6 !important;
}

.module-action--quiz {
  border-color: rgba(255, 214, 103, 0.22);
  background: rgba(255, 214, 103, 0.08) !important;
  color: #ffd667 !important;
}

.module-action ::v-deep .v-btn__content {
  width: 100%;
  justify-content: flex-start;
}

.module-action__count {
  min-width: 1.65rem;
  margin-left: auto;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.24);
  font-size: 0.75rem;
  text-align: center;
}

.module-card__secondary-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.4rem;
}

.module-secondary-action {
  min-height: 2.5rem !important;
  padding: 0 0.6rem !important;
  color: rgba(255, 255, 255, 0.68) !important;
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.8125rem;
}

.module-secondary-action ::v-deep .v-btn__content {
  white-space: normal;
}

.module-secondary-action--danger {
  color: #ff7676 !important;
}

.drag-handle {
  cursor: grab;
}

.drag-handle:active {
  cursor: grabbing;
}

.ghost-card {
  opacity: 0.4;
  background: rgba(0, 150, 64, 0.1) !important;
}

.empty-tier {
  background: rgba(255, 255, 255, 0.02);
  border: 1px dashed rgba(255, 255, 255, 0.08);
  font-size: 0.875rem;
}

.description-clip {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.text-clip-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.h-100 {
  height: 100%;
}

.gap-3 {
  gap: 0.75rem;
}

.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}

.module-dialog {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1.25rem !important;
  background: #1b1c1b !important;
  font-size: 1rem;
}

::v-deep .module-dialog-shell {
  border-radius: 1.25rem;
}

.module-dialog__header {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.5rem 1.5rem 1.35rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: radial-gradient(circle at top left, rgba(88, 211, 136, 0.13), transparent 42%), #202120;
}

.module-dialog__title-group {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  min-width: 0;
}

.module-dialog__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  flex: 0 0 3rem;
  border: 1px solid rgba(88, 211, 136, 0.25);
  border-radius: 0.85rem;
  background: rgba(88, 211, 136, 0.12);
}

.module-dialog__mark .v-icon {
  color: #7ee5a6;
}

.module-dialog__eyebrow,
.module-next-step__eyebrow {
  display: block;
  margin-bottom: 0.2rem;
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1.4;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.module-dialog__title-group h2 {
  margin: 0;
  color: #fff;
  font-size: 1.5rem;
  line-height: 1.25;
}

.module-dialog__title-group p {
  margin: 0.35rem 0 0;
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.875rem;
  line-height: 1.5;
}

.module-dialog__close {
  flex: 0 0 auto;
  margin-top: -0.25rem;
  margin-right: -0.25rem;
}

.module-dialog__body {
  display: grid;
  gap: 1rem;
  padding: 1.25rem 1.5rem 1.5rem !important;
}

.module-dialog__body ::v-deep .v-form {
  display: grid;
  gap: 1rem;
}

.dialog-section {
  padding: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.025);
}

.dialog-section__heading {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1.15rem;
}

.dialog-section__heading--with-count .v-chip {
  margin-left: auto;
}

.dialog-section__number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.8rem;
  height: 1.8rem;
  flex: 0 0 1.8rem;
  border-radius: 50%;
  background: rgba(88, 211, 136, 0.14);
  color: #7ee5a6;
  font-size: 0.8125rem;
  font-weight: 800;
}

.dialog-section__heading h3,
.module-next-step h3 {
  margin: 0;
  color: #fff;
  font-size: 1.0625rem;
  line-height: 1.35;
}

.dialog-section__heading p,
.module-next-step p {
  margin: 0.2rem 0 0;
  color: rgba(255, 255, 255, 0.58);
  font-size: 0.875rem;
  line-height: 1.5;
}

.visibility-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  margin-top: 0.85rem;
  padding: 0.9rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 0.75rem;
  background: rgba(0, 0, 0, 0.18);
}

.visibility-panel h4 {
  margin: 0;
  color: #fff;
  font-size: 0.9375rem;
}

.visibility-panel p {
  margin: 0.2rem 0 0;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.8125rem;
  line-height: 1.45;
}

.module-next-step {
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
  padding: 1rem 1.15rem;
  border: 1px solid rgba(88, 211, 136, 0.22);
  border-radius: 1rem;
  background: rgba(88, 211, 136, 0.07);
}

.module-next-step__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  flex: 0 0 2.5rem;
  border-radius: 0.7rem;
  background: rgba(88, 211, 136, 0.13);
}

.module-next-step__icon .v-icon {
  color: #7ee5a6;
}

.module-dialog__actions {
  flex: 0 0 auto;
  gap: 0.5rem;
  padding: 1rem 1.5rem !important;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: #202120;
}

.module-dialog__cancel,
.module-dialog__save,
.module-dialog__upload {
  min-height: 2.75rem !important;
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.875rem;
  font-weight: 700;
}

.module-dialog__cancel {
  margin-left: auto;
}

.module-dialog__save {
  min-width: 14rem;
  border-radius: 0.7rem;
}

.module-dialog ::v-deep .v-label,
.module-dialog ::v-deep input,
.module-dialog ::v-deep textarea,
.module-dialog ::v-deep .v-select__selection {
  font-size: 0.875rem;
}

.module-dialog ::v-deep .v-messages__message,
.module-dialog ::v-deep .v-chip__content {
  font-size: 0.75rem;
  line-height: 1.4;
}

.toolbox-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
  gap: 0.75rem;
}

.toolbox-item {
  background: rgba(20, 20, 20, 0.8) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.toolbox-icon {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.65rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.04);
}

.toolbox-preview img {
  width: 100%;
  max-height: 7.5rem;
  object-fit: cover;
  border-radius: 0.5rem;
}

.toolbox-item__meta {
  margin-top: 0.2rem;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.75rem;
}

.toolbox-item__text {
  margin-top: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: break-spaces;
  overflow-wrap: anywhere;
}

.toolbox-empty {
  padding: 1rem;
  border: 1px dashed rgba(255, 255, 255, 0.1);
  border-radius: 0.75rem;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.875rem;
  text-align: center;
}

.auto-duration {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-height: 2.5rem;
  height: 100%;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8125rem;
  line-height: 1.4;
}

.auto-duration .v-icon {
  color: #7ee5a6;
  font-size: 1.2rem;
}

@media (max-width: 37.5rem) {
  .tier-section {
    padding: 0.85rem 0.75rem 0.4rem;
  }

  .module-tile {
    flex-basis: 100%;
    min-width: 0;
    max-width: none;
  }

  .module-card__heading {
    grid-template-columns: auto auto minmax(0, 1fr);
  }

  .module-card__status {
    grid-column: 3;
  }

  .module-card__secondary-actions {
    grid-template-columns: 1fr;
  }

  .module-dialog__header {
    padding: 1.15rem;
  }

  .module-dialog__mark {
    width: 2.5rem;
    height: 2.5rem;
    flex-basis: 2.5rem;
  }

  .module-dialog__title-group {
    gap: 0.75rem;
  }

  .module-dialog__title-group h2 {
    font-size: 1.25rem;
  }

  .module-dialog__body {
    padding: 0.85rem !important;
  }

  .dialog-section {
    padding: 1rem;
  }

  .visibility-panel,
  .module-next-step {
    align-items: flex-start;
  }

  .module-dialog__actions {
    display: grid;
    grid-template-columns: 1fr;
    padding: 0.85rem !important;
  }

  .module-dialog__cancel {
    order: 2;
    margin-left: 0;
  }

  .module-dialog__save {
    min-width: 0;
    width: 100%;
  }
}
</style>
